/**
 * @fileoverview Unified comment navigation utilities for scrolling to different comment types
 * and handling various navigation scenarios across editor and renderer contexts.
 */
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';

import {
	getRendererAnnotationEventEmitter,
	getEditorAnnotationEventEmitter,
} from '@confluence/annotation-event-emitter';

import {
	checkElementExpandInEditor,
	scrollToElementInEditor,
	openInlineCommentInEditor,
} from './editorAnnotations';
import {
	scrollToComment,
	watchForCommentRepliesAndScroll,
	shouldScrollCommentThreadToTop,
	isCommentThreadReplyOutOfView,
} from './domUtils';

enum CommentType {
	INLINE = 'inline',
	GENERAL = 'general',
}

// Navigation types based on comment scrolling architecture
enum NavigationType {
	INLINE_ANNOTATION = 'inline_annotation',
	COMMENTS_PANEL = 'comments_panel',
	FOOTER_COMMENTS = 'footer_comments',
	DEEP_LINK = 'deep_link',
	KEYBOARD_SHORTCUT = 'keyboard_shortcut',
	INLINE_HEADER_ARROWS = 'inline_header_arrows',
}

// Scroll behavior options
export enum ScrollBehavior {
	INSTANT = 'instant',
	SMOOTH = 'smooth',
}

// Navigation context
export enum NavigationContext {
	EDITOR = 'editor',
	RENDERER = 'renderer',
	PANEL = 'panel',
}

// Options for navigation
export type NavigationOptions = {
	navigationType: NavigationType;
	scrollBehavior?: ScrollBehavior;
	context: NavigationContext;
	block?: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	markElement?: HTMLElement | null;
	onScrollReady?: (element: Element) => void;
	onFallback?: () => void;
};

// Scroll logical position types
type ScrollLogicalPosition = 'start' | 'center' | 'end' | 'nearest';

/**
 * Handle inline annotation navigation (editor vs renderer)
 * Scrolls to and focuses on inline comment annotations in the content
 */
export const handleInlineAnnotationNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline: ScrollLogicalPosition;
	markElement: HTMLElement | null | undefined;
	annotationId?: string;
}): boolean => {
	const { scrollBehavior, context, block, inline, markElement, annotationId } = options;

	if (!markElement) {
		return false;
	}

	// Handle different contexts
	switch (context) {
		case NavigationContext.EDITOR:
			// Check if element needs to be expanded in editor
			checkElementExpandInEditor(markElement);

			// Emit the setselectedannotation event
			const editorEmitter = getEditorAnnotationEventEmitter();
			editorEmitter.emit('setselectedannotation', markElement.id);

			// Scroll to the element in editor
			scrollToElementInEditor({
				targetElement: markElement,
				viewingRoomOffset: -100,
			});

			return true;

		case NavigationContext.RENDERER:
			// Renderer-specific logic with improved focus management
			const focusedMarkerRef = document.querySelector("[data-has-focus='true']");

			if (focusedMarkerRef?.id !== markElement.id) {
				const emitter = getRendererAnnotationEventEmitter();

				// Set focus and emit events for proper annotation highlighting
				emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
					annotationIds: [markElement.id],
					eventTarget: markElement,
				});
				emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
					annotationId: markElement.id,
				});

				// Check if element needs to be expanded in editor view
				checkElementExpandInEditor(markElement);
			}

			// Scroll to the annotation mark
			markElement.scrollIntoView({
				behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
				block,
				inline,
			});
			return true;

		case NavigationContext.PANEL:
			// For panel context, ensure annotation is visible and highlighted
			if (annotationId) {
				// Emit events to ensure the annotation is properly focused
				const emitter = getRendererAnnotationEventEmitter();
				emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
					annotationId,
				});
			}

			// Scroll the annotation into view
			markElement.scrollIntoView({
				behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
				block,
				inline,
			});
			return true;

		default:
			return false;
	}
};

/**
 * Handle comments panel navigation (inline + footer threads)
 * Scrolls to comment threads within the comments panel container
 */
export const handleCommentsPanelNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	annotationId: string;
	commentType?: 'inline' | 'footer';
	scrollToEnd?: boolean;
	hasKeyPressed?: boolean;
	annotationClicked?: boolean;
	scrollOnLoad?: boolean;
	commentContainerToFocus?: string;
}): boolean => {
	const {
		scrollBehavior,
		context,
		block,
		annotationId,
		commentType,
		scrollToEnd = false,
		hasKeyPressed = false,
		annotationClicked = false,
		scrollOnLoad = false,
		commentContainerToFocus,
	} = options;

	if (context !== NavigationContext.PANEL) {
		return false;
	}
	// Define panel container and thread selectors
	const commentsPanelListContainerId = 'comments-panel-list-container';
	const commentThreadContainerId = `comment-thread-${annotationId}-container`;

	// Get the panel container
	const panelContainer = document.querySelector(`[data-testid='${commentsPanelListContainerId}']`);
	if (!panelContainer) {
		return false;
	}

	// Look for the specific comment thread container
	const commentThreadElement = document.querySelector(
		`[data-testid='${commentThreadContainerId}']`,
	);

	const performScroll = (element: Element) => {
		// Handle specific container focus first
		if (commentContainerToFocus) {
			const focusContainer = document.querySelector(`[data-testid='${commentContainerToFocus}']`);
			if (focusContainer) {
				focusContainer.scrollIntoView({
					behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
					block: 'center',
					inline: 'center',
				});
				return;
			}
		}

		// Check for reply button focus
		const activeElement = document.activeElement;
		const isReplyButton = activeElement?.getAttribute('data-testid') === 'comment-reply-button';
		if (isReplyButton) {
			const replyContainer = document.querySelector(
				'[data-testid=comments-panel-new-reply-container]',
			);
			if (replyContainer) {
				replyContainer.scrollIntoView({
					behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
					block: 'center',
					inline: 'center',
				});
				return;
			}
		}

		// Check if element is already fully visible - if so, don't scroll
		const elementRect = element.getBoundingClientRect();
		const panelContainer = document.querySelector('[data-testid="comments-panel-list-container"]');
		const panelRect = panelContainer?.getBoundingClientRect();

		if (panelRect) {
			const isFullyVisible =
				elementRect.top >= panelRect.top && elementRect.bottom <= panelRect.bottom;

			if (isFullyVisible) {
				return;
			}
		}

		// Handle scroll behavior based on trigger type
		if (hasKeyPressed || annotationClicked || scrollOnLoad) {
			let scrollBlock: ScrollLogicalPosition = block;

			// Determine scroll position based on context and comment type
			if (scrollToEnd) {
				// Check if the comment thread reply is out of view
				const commentTypeEnum = commentType === 'inline' ? CommentType.INLINE : CommentType.GENERAL;
				const isOutOfView = isCommentThreadReplyOutOfView(element, commentTypeEnum);
				if (isOutOfView) {
					scrollBlock = 'end';
				}
			} else {
				// Check if we should scroll to top
				const commentTypeEnum = commentType === 'inline' ? CommentType.INLINE : CommentType.GENERAL;
				const shouldScrollToTop = shouldScrollCommentThreadToTop(element, commentTypeEnum);
				if (shouldScrollToTop) {
					scrollBlock = 'start';
				}
			}

			element.scrollIntoView({
				behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
				block: scrollBlock,
			});
		} else {
			// Default scroll behavior
			element.scrollIntoView({
				behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
				block,
			});
		}
	};

	// If comment thread exists, scroll immediately
	if (commentThreadElement) {
		performScroll(commentThreadElement as Element);
		return true;
	}

	// Use optimized observer to watch for comment thread container creation
	watchForCommentRepliesAndScroll({
		targetCommentId: annotationId,
		onScrollReady: (commentElement) => {
			performScroll(commentElement);
		},
		timeoutMs: 5000,
	});

	return true;
};

/**
 * WIP: Handle footer comments navigation (general page-level threads)
 */
export const handleFooterCommentsNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline: ScrollLogicalPosition;
}): boolean => {
	const { context } = options;

	if (context !== 'panel') {
		return false;
	}

	return false;
};

/**
 * Handle deep link navigation (URL params)
 * Simple: scroll to comment once it's rendered on screen
 */
export const handleDeepLinkNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	markElement: HTMLElement | null | undefined;
	commentId: string;
}): boolean => {
	const { scrollBehavior, block, inline, commentId, markElement, context } = options;

	if (!commentId || !markElement) {
		return false;
	}

	// Check if comment already exists in DOM
	const existingComment = document.querySelector(`[data-comment-id="${commentId}"]`);

	if (existingComment) {
		// Comment is already rendered, scroll to it immediately
		existingComment.scrollIntoView({
			behavior: scrollBehavior,
			block,
			inline,
		});
		return true;
	} else {
		// we need to click on the annotation in the editor if the comment is not yet rendered
		if (context === NavigationContext.EDITOR && markElement) {
			openInlineCommentInEditor({
				targetElement: markElement,
				targetElementID: markElement.id,
				shouldScroll: false,
			});
		}
		// Comment not yet rendered, use DOM resolver to watch for it
		watchForCommentRepliesAndScroll({
			targetCommentId: commentId,
			onScrollReady: (commentElement) => {
				commentElement.scrollIntoView({
					behavior: scrollBehavior,
					block,
					inline,
				});
			},
			markElement,
		});
		return true;
	}

	return false;
};

/**
 * WIP: Handle keyboard shortcut navigation (next/previous/unread)
 */
export const handleKeyboardShortcutNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline: ScrollLogicalPosition;
}): boolean => {
	const { context } = options;

	if (context !== 'panel') {
		return false;
	}

	return false;
};

/**
 * Handle inline header arrows navigation (UI navigation)
 * Uses scrollToComment once the new comment element is available
 */
export const handleInlineHeaderArrowsNavigation = (options: {
	previousMarkerRef: string; // Previous comment marker ref
	nextMarkerRef: string; // Next comment marker ref
	context: 'editor' | 'renderer' | 'panel';
}): boolean => {
	const { previousMarkerRef, nextMarkerRef, context } = options;

	// Get the next comment element
	const nextMarkElement = document.getElementById(nextMarkerRef);
	if (!nextMarkElement) {
		return false;
	}

	// Get the previous and next comment elements
	const previousCommentElement = document.getElementById(previousMarkerRef);
	const nextCommentElement = document.getElementById(nextMarkerRef);

	if (previousCommentElement && nextCommentElement) {
		if (context === NavigationContext.EDITOR) {
			// For editor: use scrollableContainer.scrollBy with difference offset
			const scrollableContainer = document.querySelector(
				'.fabric-editor-popup-scroll-parent',
			) as HTMLElement;
			if (scrollableContainer) {
				// Calculate difference between current and previous comment positions
				const currentRect = nextCommentElement.getBoundingClientRect();
				const previousRect = previousCommentElement.getBoundingClientRect();
				const differenceOffset = currentRect.top - previousRect.top;

				scrollableContainer.scrollBy(0, differenceOffset);
			}
		} else {
			// For renderer: use scrollToComment
			scrollToComment(nextCommentElement, previousCommentElement, true, false);
		}
	}

	return true;
};
